import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaGoogle } from 'react-icons/fa';
import FeedbackCard from './FeedbackCard';

const DURATION = 5000;
const STEP = 50;
const INCREMENT = 100 / (DURATION / STEP);

const Carousel = ({ feedback: raw }) => {
  const feedback = raw.length === 1 ? [...raw, ...raw, ...raw] : raw;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const startX = useRef(null);
  const containerRef = useRef(null);

  const mod = (n, m) => ((n % m) + m) % m;

  const reset = useCallback(() => setProgress(0), []);
  const tick = useCallback(() => {
    setProgress((p) => {
      if (p >= 100) {
        setIndex((i) => mod(i + 1, feedback.length));
        return 0;
      }
      return p + INCREMENT;
    });
  }, [feedback.length]);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(tick, STEP);
    return () => clearInterval(timer.current);
  }, [paused, tick]);

  const go = useCallback(
    (delta) => {
      setIndex((i) => mod(i + delta, feedback.length));
      reset();
    },
    [feedback.length, reset]
  );
  const next = useCallback(() => go(1), [go]);
  const prev = useCallback(() => go(-1), [go]);

  const handleTouchStart = (e) => (startX.current = e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const diff = startX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    const el = containerRef.current;
    el?.addEventListener('keydown', onKey);
    return () => el?.removeEventListener('keydown', onKey);
  }, [prev, next]);

  return (
    <section
      ref={containerRef}
      tabIndex={0}
      aria-label="Customer feedback carousel"
      className="relative isolate overflow-hidden bg-white py-16 sm:py-20 lg:py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* backdrop */}
      <div className="absolute inset-0 -z-10 animate-[pulse_12s_ease-in-out_infinite] opacity-20">
        <div className="absolute top-1/4 left-1/4 h-40 w-40 rounded-full bg-[#0e6d87]/10 blur-3xl sm:h-56 sm:w-56" />
        <div className="absolute bottom-1/4 right-1/4 h-48 w-48 rounded-full bg-[#0e6d87]/10 blur-3xl sm:h-72 sm:w-72" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
          Happy Customers, Happy Us!
        </h2>

        <div className="relative mx-auto mt-12 max-w-lg sm:mt-16 sm:max-w-2xl lg:max-w-4xl">
          <div className="w-full overflow-hidden rounded-2xl">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {feedback.map((item, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-2 sm:px-4">
                    <div className="mx-auto max-w-md rounded-2xl bg-white/70 backdrop-blur-md p-6 shadow-xl ring-1 ring-[#0e6d87]/20 sm:p-8 lg:p-10">
                      <FeedbackCard {...item} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* arrows hidden below 640 px */}
          <Arrow direction="left" onClick={prev} />
          <Arrow direction="right" onClick={next} />

          {/* progress */}
          <div className="mt-8 h-1 w-full rounded-full bg-[#0e6d87]/20 sm:mt-10">
            <div
              className="h-full rounded-full bg-[#0e6d87] transition-[width] duration-75 linear"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* dots */}
          <div className="mt-4 flex justify-center space-x-2 sm:mt-6 sm:space-x-3">
            {feedback.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setIndex(i);
                  reset();
                }}
                aria-label={`Go to review ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ease-out sm:h-2
                  ${i === index ? 'w-6 bg-[#0e6d87] sm:w-8' : 'w-1.5 bg-[#0e6d87]/40 hover:bg-[#0e6d87]/70 sm:w-2'}`}
              />
            ))}
          </div>
        </div>

        <div className="mt-12 flex justify-center sm:mt-16">
          <a
            href="https://g.co/kgs/kym1uki"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#0e6d87] px-5 py-2.5 text-sm font-semibold text-white shadow-md ring-1 ring-[#0e6d87] transition hover:bg-[#0b566a] sm:px-6 sm:py-3 sm:text-base"
          >
            <FaGoogle className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:scale-110" />
            View More Reviews on Google
          </a>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500 sm:hidden">
          Swipe to explore
        </p>
      </div>
    </section>
  );
};

const Arrow = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    aria-label={`${direction} review`}
    className={`hidden sm:block absolute top-1/2 -translate-y-1/2 rounded-full bg-[#0e6d87]/10 p-2 text-[#0e6d87] backdrop-blur-sm transition hover:bg-[#0e6d87]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0e6d87] sm:p-3
      ${direction === 'left' ? 'left-0 -translate-x-full sm:left-2 lg:left-4' : 'right-0 translate-x-full sm:right-2 lg:right-4'}`}
  >
    <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      {direction === 'left' ? (
        <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  </button>
);

export default Carousel;